import { useEffect, useState } from 'preact/hooks';
import { s3DataPrefix } from '../config/app';
import { redirectCampaignExpired } from '../helpers/utils';

import useVendor from './useVendor';
import useCampaignDiscount from './useCampaignDiscount';
import useValidCampaignId from './useValidCampaignId';
import { HookState } from '../types/hooks';
import { Campaign } from '../types/campaign';

/**
 * Calculate vendor name based on current window location
 * @return {String}
 */
const useCampaignData = (): HookState<Campaign> => {
    const [status, setStatus] = useState<HookState<Campaign>>({
        loading: true,
        error: false,
        data: null,
    });

    const vendor = useVendor();
    const campaignId = useValidCampaignId();
    const campaignDiscount = useCampaignDiscount();

    useEffect(() => {
        (async () => {
            // Declaring these variables inside try{...} statement
            // Otherwise this leads to a babel compilation error when running Jest tests
            let response, campaignData: { campaign: Campaign };

            try {
                response = await fetch(`/${s3DataPrefix}/offers/${campaignId}.json`);
                if (!response.ok) {
                    throw new Error(`${response.status} ${response.statusText}`);
                }
                campaignData = await response.json();

                checkCampaignValidity(campaignData.campaign, vendor);

                const discountData = await campaignDiscount.getAllCategoriesDiscount();
                mergeCampaignDataWithDiscount(campaignData.campaign, discountData, vendor);

                setStatus({ loading: false, error: false, data: campaignData.campaign });
            } catch (error: any) {
                redirectCampaignExpired(vendor);
                console.error(`${error.message}`);
                setStatus({ loading: false, error: true, data: null });
            }
        })();
    }, [vendor, campaignId, campaignDiscount]);

    return status;
};

/**
 * Test the campaign data on presence of offers for the vendor and the runtime of the campaign
 * @param {Object} campaignData
 * @param {String} vendor
 */
function checkCampaignValidity(campaignData, vendor: string): void {
    // if this vendor is not used for the campaign or does not have LP data, redirect to the offer expired page
    if (
        !Object.keys(campaignData.landingPageOffers[vendor]).length ||
        !Object.keys(campaignData.landingPageOffers[vendor].promotions).length
    ) {
        throw new Error(`${vendor} does not have the correct data`);
    }

    // check if the campaign expiration date is not in the past
    // @TODO add support for campaign extensions when they are officially supported
    const currentMoment = new Date().valueOf();
    const campaignActivationMoment = new Date(campaignData.overallActivationDate).getTime();
    const campaignExpirationMoment = new Date(campaignData.overallExpirationDate).getTime();

    if (campaignActivationMoment >= currentMoment || campaignExpirationMoment <= currentMoment) {
        if (process.env.APP_ENV !== 'dev') {
            throw new Error(`${vendor} campaign is not valid`);
        }
    }
}

/**
 * Add discount data that fetched from campaigndisplayclient to existing campaign data
 *
 * @param {Object} campaign
 * @param {Object} discount
 * @param {String} vendor
 */
function mergeCampaignDataWithDiscount(campaign, discount, vendor: string): void {
    const offers = campaign.landingPageOffers[vendor];

    offers.promotions.forEach((promotion) => {
        const articleTypes = Object.keys(promotion.offers);
        articleTypes.forEach((articleType) => {
            const lowerCaseArticleType = articleType.toLowerCase();

            promotion.offers[articleType] = {
                amount: promotion.offers[articleType],
                discount:
                    discount[lowerCaseArticleType] && discount[lowerCaseArticleType].campaign_uuid === campaign.uuid
                        ? discount[lowerCaseArticleType]
                        : null,
            };
        });

        const lowerCaseFirstArticleType = articleTypes[0].toLowerCase();
        if (
            articleTypes.length === 1 &&
            discount[lowerCaseFirstArticleType] &&
            discount[lowerCaseFirstArticleType].campaign_uuid === campaign.uuid
        ) {
            promotion.promoLabel = discount[lowerCaseFirstArticleType].discount_label;
        }
    });
}

export default useCampaignData;
